import { useNavigate } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const BADGE_CONFIG = {
  UNRESOLVED_EXTERNAL_ID: { label: 'Unresolved External ID', short: 'Unresolved', color: '#dc2626', bg: '#fef2f2' },
  NEEDS_REVIEW: { label: 'Mapping Needs Review', short: 'Review', color: '#d97706', bg: '#fffbeb' },
  LOW_MAPPING_CONFIDENCE: { label: 'Low Mapping Confidence', short: 'Low Conf', color: '#d97706', bg: '#fffbeb' },
  MISSING_SKU_SIZE: { label: 'Missing SKU Size', short: 'No SKU', color: '#d97706', bg: '#fffbeb' },
  MISSING_UNIT_NORM: { label: 'Missing Unit Norm', short: 'No Unit', color: '#6366f1', bg: '#eef2ff' },
  INCONSISTENT_UNITS: { label: 'Inconsistent Units', short: 'Units', color: '#d97706', bg: '#fffbeb' },
  XREF_DEPENDENCY: { label: 'XREF Bridge Dependency', short: 'XREF', color: '#6366f1', bg: '#eef2ff' },
};

/**
 * Build a lookup index from raw data-quality alerts for fast entity matching.
 * Returns { byEntityId: { id -> [alerts] }, byIngredientName: { name -> [alerts] } }
 */
export function buildAlertLookup(alerts) {
  const byEntityId = {};
  const byIngredientName = {};

  const add = (map, key, alert) => {
    if (!key) return;
    const k = String(key).toLowerCase();
    if (!map[k]) map[k] = [];
    map[k].push(alert);
  };

  for (const alert of alerts || []) {
    const sm = alert.supporting_metrics || {};

    // Index by entity_id for direct ID matches
    if (alert.entity_id) {
      add(byEntityId, alert.entity_id, alert);
    }

    // Also index by ingredient_name from supporting_metrics
    const ingName =
      sm.ingredient_name || sm.cr_ingredient_name || sm.sample_item_name;
    if (ingName) {
      add(byIngredientName, ingName, alert);
    }
  }

  return { byEntityId, byIngredientName };
}

/**
 * Get deduplicated badges for a specific ingredient.
 */
export function getBadgesForEntity(lookup, entityId, entityName) {
  const raw = [
    ...(lookup.byEntityId[String(entityId || '').toLowerCase()] || []),
    ...(lookup.byIngredientName[String(entityName || '').toLowerCase()] || []),
  ];

  // Deduplicate by alert_type, aggregate counts
  const grouped = {};
  for (const alert of raw) {
    const t = alert.alert_type;
    if (!grouped[t]) {
      grouped[t] = { alert_type: t, severity: alert.severity, message: alert.message, count: 1 };
    } else {
      grouped[t].count++;
    }
  }
  return Object.values(grouped);
}

/**
 * Compact inline quality badges with tooltip explanations.
 * Renders small pills next to entity names. Click navigates to Intelligence Hub.
 */
const QualityBadges = ({ badges, className = '' }) => {
  const navigate = useNavigate();

  if (!badges?.length) return null;

  return (
    <TooltipProvider delayDuration={150}>
      <div className={`flex flex-wrap gap-1 ${className}`} data-testid="quality-badges">
        {badges.map((badge) => {
          const config = BADGE_CONFIG[badge.alert_type];
          if (!config) return null;
          return (
            <Tooltip key={badge.alert_type}>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/intelligence');
                  }}
                  className="inline-flex items-center px-1.5 py-px text-[10px] font-semibold rounded leading-4 cursor-pointer transition-opacity hover:opacity-75"
                  style={{
                    background: config.bg,
                    color: config.color,
                    border: `1px solid ${config.color}25`,
                  }}
                  data-testid={`quality-badge-${badge.alert_type.toLowerCase()}`}
                >
                  {config.short}
                  {badge.count > 1 ? ` (${badge.count})` : ''}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs z-[100]">
                <p className="font-semibold text-xs">{config.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                  {badge.message}
                </p>
                <p className="text-[10px] text-blue-500 mt-1">
                  Click to view in Intelligence Hub
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
};

export default QualityBadges;
